// src/components/Shared/FileUploader.tsx
import React, { useCallback, useState } from 'react';
import { useDropzone, Accept } from 'react-dropzone';
import { Upload, X, FilePlus, FileX, Loader2, AlertCircle } from 'lucide-react';

interface CustomErrorMessages {
    fileTooLarge?: string;
    fileInvalidType?: string;
    totalSizeExceeded?: string;
}

interface FileUploaderProps {
    onFileUpload: (files: File | File[]) => void;
    multiple?: boolean;
    loading?: boolean;
    error?: any;
    acceptedTypes?: Accept | null;
    maxSizeMB?: number;
    totalMaxSizeMB?: number | null;
    instructionText?: string;
    supportedFormats?: string;
    customErrorMessages?: CustomErrorMessages;
}

const FileUploader: React.FC<FileUploaderProps> = ({
    onFileUpload,
    multiple = false,
    loading = false,
    error = null,
    acceptedTypes = null,
    maxSizeMB = 50,
    totalMaxSizeMB = null,
    instructionText = "Drag and drop a file here, or click to select",
    supportedFormats = "Supported formats vary by tool",
    customErrorMessages = {}
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes
    const totalMaxSize = totalMaxSizeMB ? totalMaxSizeMB * 1024 * 1024 : null;

    // Format file size in a readable way
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            // Handle rejected files
            const errors = rejectedFiles.map(file => {
                const error = file.errors[0];
                if (error.code === 'file-too-large') {
                    return customErrorMessages.fileTooLarge ?
                        customErrorMessages.fileTooLarge.replace('{{filename}}', file.file.name).replace('{{maxSize}}', formatFileSize(maxSize)) :
                        `${file.file.name} is too large. Max size is ${formatFileSize(maxSize)}.`;
                } else if (error.code === 'file-invalid-type') {
                    return customErrorMessages.fileInvalidType ?
                        customErrorMessages.fileInvalidType.replace('{{filename}}', file.file.name) :
                        `${file.file.name} is not a supported file type.`;
                }
                return `${file.file.name}: ${error.message}`;
            });

            setUploadError(errors.join(' '));
            return;
        }

        // Check total file size for multiple files
        if (totalMaxSize && multiple) {
            const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
            if (totalSize > totalMaxSize) {
                const errorMessage = customErrorMessages.totalSizeExceeded ?
                    customErrorMessages.totalSizeExceeded.replace('{{totalSize}}', formatFileSize(totalSize)).replace('{{maxTotalSize}}', formatFileSize(totalMaxSize)) :
                    `Total size (${formatFileSize(totalSize)}) exceeds the limit of ${formatFileSize(totalMaxSize)}.`;
                setUploadError(errorMessage);
                return;
            }
        }

        setUploadError(null);

        if (multiple) {
            setSelectedFiles(acceptedFiles);
        } else {
            setSelectedFiles(acceptedFiles.slice(0, 1));
        }
    }, [multiple, maxSize, totalMaxSize, customErrorMessages]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple,
        accept: acceptedTypes || undefined,
        maxSize,
        disabled: loading
    });

    const handleUpload = () => {
        if (selectedFiles.length === 0) {
            setUploadError('Please select a file first.');
            return;
        }

        setUploadError(null);
        onFileUpload(multiple ? selectedFiles : selectedFiles[0]);
    };

    const removeFile = (index: number) => {
        const newFiles = [...selectedFiles];
        newFiles.splice(index, 1);
        setSelectedFiles(newFiles);
    };

    const clearFiles = () => {
        setSelectedFiles([]);
        setUploadError(null);
    };

    const totalSelectedSize = selectedFiles.reduce((total, file) => total + file.size, 0);

    return (
        <div className="mb-8">
            <div
                {...getRootProps()}
                className={`relative flex flex-col items-center justify-center h-[300px] border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300 overflow-hidden bg-white hover:border-primary hover:bg-blue-600/5 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-primary dark:hover:bg-blue-600/10 ${isDragActive ? 'border-primary bg-blue-600/5 dark:bg-blue-600/15' : 'border-slate-200'} ${loading ? 'opacity-70 cursor-not-allowed' : ''} max-md:h-[180px] max-[480px]:h-[150px]`}
            >
                <input {...getInputProps()} />

                <div className="flex flex-col items-center text-center p-8 max-md:p-4">
                    <Upload className="text-primary mb-6 opacity-80 max-md:h-8 max-md:w-8 max-md:mb-4 max-[480px]:h-7 max-[480px]:w-7 max-[480px]:mb-3" size={48} />
                    <p className="text-lg font-medium text-slate-700 mb-2 dark:text-slate-200 max-md:text-base max-[480px]:text-sm">{instructionText}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-md:text-[13px] max-[480px]:text-xs">{supportedFormats}</p>
                    <p className="text-xs text-slate-500 mt-1 opacity-90 dark:text-slate-400">Maximum file size: {formatFileSize(maxSize)}</p>
                    {totalMaxSize && multiple && (
                        <p className="text-xs text-slate-500 mt-1 opacity-90 dark:text-slate-400">Maximum total size: {formatFileSize(totalMaxSize)}</p>
                    )}

                    {isDragActive && (
                        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-10 animate-in fade-in duration-200 dark:bg-slate-900/90">
                            <FilePlus size={48} className="text-primary" />
                            <p className="mt-4 font-medium text-primary">Drop file(s) here...</p>
                        </div>
                    )}

                    {loading && (
                        <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center z-10 dark:bg-slate-900/95">
                            <Loader2 size={48} className="animate-spin text-primary" />
                            <p className="mt-4 font-medium text-primary">Uploading file(s)...</p>
                        </div>
                    )}
                </div>
            </div>

            {uploadError && (
                <div className="flex items-center gap-2 px-4 py-3 mt-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                    <AlertCircle size={16} />
                    <span>{uploadError}</span>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 px-4 py-3 mt-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                    <FileX size={16} />
                    <span>{error.message || 'An error occurred'}</span>
                </div>
            )}

            {selectedFiles.length > 0 && (
                <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden dark:bg-slate-800 dark:border-slate-700 max-md:mt-4">
                    <div className="flex justify-between items-center p-3 border-b border-slate-200 bg-white relative dark:border-slate-700 dark:bg-slate-800 max-md:p-2 max-sm:flex-wrap">
                        <h4 className="text-[15px] font-semibold text-slate-700 m-0 dark:text-slate-200 max-sm:mr-auto">Selected File{selectedFiles.length > 1 ? 's' : ''}</h4>
                        {multiple && <span className="absolute left-1/2 -translate-x-1/2 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full border border-slate-200 dark:text-slate-400 dark:bg-slate-900 dark:border-slate-700 max-sm:static max-sm:translate-x-0 max-sm:ml-2 max-sm:text-[11px]">Total: {formatFileSize(totalSelectedSize)}</span>}
                        <button
                            className="inline-flex items-center gap-1 px-2 py-1 bg-transparent border-none text-red-500 text-xs rounded hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:hover:bg-red-500/20"
                            onClick={clearFiles}
                            disabled={loading}
                            type="button"
                        >
                            <X size={14} />
                            Clear All
                        </button>
                    </div>

                    <ul className="list-none p-0 m-0 max-h-[200px] overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                            <li key={index} className="flex items-center justify-between px-4 py-3 border-b border-slate-200 last:border-b-0 dark:border-slate-700 max-md:px-2">
                                <span className="flex-1 text-sm text-slate-700 break-all mr-4 dark:text-slate-200 max-[480px]:max-w-[180px]">{file.name}</span>
                                <span className="text-xs text-slate-500 mr-4 whitespace-nowrap dark:text-slate-400">{formatFileSize(file.size)}</span>
                                <button
                                    className="p-1 bg-transparent border-none text-slate-500 cursor-pointer rounded transition-colors hover:text-red-500 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-400 dark:hover:bg-red-500/20"
                                    onClick={() => removeFile(index)}
                                    disabled={loading}
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <button
                        className="flex items-center justify-center gap-2 w-full p-3.5 bg-primary text-white border-none font-medium cursor-pointer transition-colors hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed max-md:mt-4 max-md:p-3"
                        onClick={handleUpload}
                        disabled={loading || selectedFiles.length === 0}
                        type="button"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                <span>Uploading...</span>
                            </>
                        ) : (
                            <>
                                <Upload size={16} />
                                <span>Process {selectedFiles.length > 1 ? 'Files' : 'File'}</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default FileUploader;
